import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod/v4";
import {
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronUp,
  Clock,
  XIcon,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { HTTPError } from "ky";
import type { MeetingBot } from "@/lib/queries/meetings";
import {
  featuresQueryOptions,
  sortFeaturesByDisplayOrder,
} from "@/lib/queries/meetings";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  generateDefaultValue,
  requiredString,
  requiredUrl,
} from "@/lib/utils/zod";
import { api } from "@/lib/http";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { setLocalStorageValue } from "@/lib/hooks/localStorage";
import {
  getSessionStorageValue,
  removeSessionStorageValue,
  setSessionStorageValue,
} from "@/lib/hooks/sessionStorage";
import {
  isZoomConnectionRequiredError,
  showZoomConnectionRequiredToast,
} from "@/lib/utils/zoom-connection";
import { asBillingError } from "@/lib/http/billing";
import { invalidateBillingUsage } from "@/lib/queries/billing";
import { userInfoQueryOptions } from "@/lib/queries/auth";

export const Route = createFileRoute("/_app/create-a-bot/")({
  component: RouteComponent,
  pendingComponent: CreateBotPageSkeleton,
  async loader({ context: { queryClient } }) {
    queryClient.ensureQueryData(featuresQueryOptions());
    return {
      myBotName: await api.processing
        .raw<{
          id: string;
          organization_id: string;
          user_id: string;
          default_bot_name: string;
        }>("api/v1/org-settings/my-bot-name")
        .json(),
      // Read without clearing: the callback preloads this route and
      // defaultPreloadStaleTime is 0, so the loader runs again for the real
      // navigation and a consuming read would hand it an empty draft.
      restoredDraft: getSessionStorageValue(
        CREATE_BOT_DRAFT_KEY,
        createBotDraftSchema,
      ),
    };
  },
});
const storedBotNameSchema = z.string().trim().min(1);
const HTTP_URL_SCHEME_ERROR = "URL scheme should be 'http' or 'https'";
const createBotSchema = z.object({
  meetingName: requiredString("Meeting name is required"),
  meetingUrl: requiredUrl("Enter a valid URL").refine(
    (url) => ["http:", "https:"].includes(new URL(url).protocol),
    { error: HTTP_URL_SCHEME_ERROR },
  ),
  botName: storedBotNameSchema,
  joinAt: z.date().optional(),
  features: z
    .record(z.string(), z.boolean())
    .refine(
      (r) => Object.values(r).reduce((acc, b) => acc + Number(b), 0) > 0,
      { error: "At least one feature must be selected" },
    ),
});

type CreateBotFormData = z.infer<typeof createBotSchema>;

const CREATE_BOT_DRAFT_KEY = "create-bot-draft";

/** Deliberately laxer than createBotSchema — a draft is allowed to be invalid */
const createBotDraftSchema = z.object({
  meetingName: z.string(),
  meetingUrl: z.string(),
  botName: z.string(),
  joinAt: z.date().optional(),
  features: z.record(z.string(), z.boolean()),
});

type CreateBotPayload = {
  meeting_name: string;
  meeting_url: string;
  bot_name: string;
  entry_announcement: boolean;
  auto_leave_on_silence: boolean;
  join_at?: string;
  features?: Array<string>;
};

function getBackendFieldError(
  detail: unknown,
  fieldName: string,
): string | null {
  if (!Array.isArray(detail)) return null;

  for (const issue of detail) {
    if (!issue || typeof issue !== "object") continue;

    const location = "loc" in issue ? issue.loc : null;
    const message = "msg" in issue ? issue.msg : null;

    if (
      Array.isArray(location) &&
      location.at(-1) === fieldName &&
      typeof message === "string"
    ) {
      return message;
    }
  }

  return null;
}

function CreateBotPageSkeleton() {
  return (
    <div
      className="w-full"
      role="status"
      aria-label="Loading create an agent form"
    >
      <div className="mb-6 space-y-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-5 w-full max-w-xl" />
      </div>

      <Card>
        <CardContent className="space-y-6 pt-6">
          <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-5 w-28" />
                <Skeleton className="h-10 w-full" />
                {index > 1 && <Skeleton className="h-5 w-72 max-w-full" />}
              </div>
            ))}
          </div>

          <div className="space-y-4">
            <Skeleton className="h-5 w-40" />
            {Array.from({ length: 3 }, (_, index) => (
              <div
                key={index}
                className="flex min-h-[90px] items-center justify-between gap-4 rounded-lg border border-border p-4"
              >
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-6 w-44 max-w-full" />
                  <Skeleton className="h-5 w-80 max-w-full" />
                </div>
                <Skeleton className="h-6 w-11 shrink-0 rounded-full" />
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Skeleton className="h-10 w-32" />
          </div>
        </CardContent>
      </Card>
      <span className="sr-only">Loading...</span>
    </div>
  );
}

function RouteComponent() {
  const { myBotName, restoredDraft } = Route.useLoaderData();
  const [settingsOpen, setSettingsOpen] = useState(true);
  const queryClient = useQueryClient();
  const userInfo = useSuspenseQuery(userInfoQueryOptions);

  const featuresQuery = useSuspenseQuery(featuresQueryOptions());

  const features = sortFeaturesByDisplayOrder(featuresQuery.data.items);

  const form = useForm({
    resolver: zodResolver(createBotSchema),
    defaultValues: restoredDraft ?? {
      ...generateDefaultValue(createBotSchema),
      botName: myBotName.default_bot_name,
      joinAt: undefined,
      features: featuresQuery.data.items.reduce<Record<string, boolean>>(
        (acc, feature) => {
          acc[feature.label] = feature.is_active;
          return acc;
        },
        {},
      ),
    },
  });

  useEffect(() => {
    removeSessionStorageValue(CREATE_BOT_DRAFT_KEY);
  }, []);

  const navigate = useNavigate();
  const createBotMutation = useMutation({
    async mutationFn(payload: CreateBotPayload) {
      const response = await api.processing.raw.post<MeetingBot>(
        "api/v1/meetings/",
        {
          json: payload,
        },
      );
      return response.json();
    },
    async onSuccess(bot) {
      toast.success("Bot created successfully!");
      await queryClient.invalidateQueries({ queryKey: ["meetings"] });
      await invalidateBillingUsage(queryClient);

      if (!bot.join_at)
        setTimeout(
          () =>
            navigate({
              to: "/meetings/$meetingId",
              params: { meetingId: bot.id },
            }),
          500,
        );
      else form.reset();
    },
    async onError(error, variables) {
      const billingError = await asBillingError(error);
      if (billingError?.code === "PAYMENT_SERVICE_UNAVAILABLE") {
        toast.error(
          "Billing is temporarily unavailable. Please try again in a moment.",
          {
            action: {
              label: "Retry",
              onClick: () => createBotMutation.mutate(variables),
            },
          },
        );
        return;
      }
      if (
        billingError?.code === "QUOTA_EXCEEDED" ||
        billingError?.code === "SUBSCRIPTION_SUSPENDED"
      ) {
        const reset = billingError.period_end
          ? new Date(billingError.period_end).toLocaleDateString()
          : null;
        const description =
          billingError.code === "QUOTA_EXCEEDED"
            ? `${billingError.message} ${Math.round(billingError.minutes_remaining ?? 0)} minutes remaining.${reset ? ` Allowance resets ${reset}.` : ""}`
            : billingError.message;
        toast.error(description, {
          action: userInfo.data.org_admin
            ? {
                label: "Billing",
                onClick: () => void navigate({ to: "/settings/billing" }),
              }
            : undefined,
        });
        return;
      }
      if (isZoomConnectionRequiredError(error)) {
        await showZoomConnectionRequiredToast(error, () => {
          setSessionStorageValue(
            CREATE_BOT_DRAFT_KEY,
            form.getValues(),
            createBotDraftSchema,
          );
        });
        return;
      }
      if (error instanceof HTTPError) {
        try {
          const body: unknown = await error.response.json();
          const detail =
            body && typeof body === "object" && "detail" in body
              ? body.detail
              : body;

          if (error.response.status === 422) {
            const meetingUrlError = getBackendFieldError(detail, "meeting_url");
            if (meetingUrlError) {
              form.setError(
                "meetingUrl",
                { type: "server", message: meetingUrlError },
                { shouldFocus: true },
              );
              return;
            }
          }

          if (typeof detail === "string") {
            toast.error(detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to create an agent");
    },
  });

  function onCreateBot(data: CreateBotFormData) {
    const enabledFeatureIds = features
      .filter((feature) => data.features[feature.label])
      .map((feature) => feature.id);

    // Store the agent name in localStorage for future use
    setLocalStorageValue("botName", data.botName, storedBotNameSchema);

    createBotMutation.mutate({
      meeting_name: data.meetingName,
      meeting_url: data.meetingUrl,
      bot_name: data.botName,
      entry_announcement: true,
      auto_leave_on_silence: true,
      join_at: data.joinAt?.toISOString(),
      features: enabledFeatureIds,
    });
  }

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">
          Create an Agent
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Fill in the meeting details below and your bot will join immediately
          or at a scheduled time.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onCreateBot)}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="meetingName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Meeting Name <span className="text-pink-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Enter meeting name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="botName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Agent Name <span className="text-pink-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Enter agent name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="meetingUrl"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>
                        Meeting URL <span className="text-pink-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://meet.google.com/sample"
                          {...field}
                        />
                      </FormControl>
                      {fieldState.error ? (
                        <FormMessage />
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Enter a valid Zoom, Google Meet, or Teams URL.
                        </p>
                      )}
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="joinAt"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Join at</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                format(field.value, "MM/dd/yyyy HH:mm")
                              ) : (
                                <span>mm/dd/yyyy --:--</span>
                              )}
                              {field.value && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="ml-auto h-4 w-4 p-0"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    field.onChange(undefined);
                                  }}
                                >
                                  <XIcon className="h-4 w-4 opacity-50 hover:opacity-100" />
                                </Button>
                              )}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              if (date) {
                                const newDate = new Date(date);
                                if (field.value) {
                                  newDate.setHours(field.value.getHours());
                                  newDate.setMinutes(field.value.getMinutes());
                                }
                                field.onChange(newDate);
                              } else {
                                field.onChange(undefined);
                              }
                            }}
                            disabled={(date) =>
                              date < new Date(new Date().setHours(0, 0, 0, 0))
                            }
                          />
                          <div className="border-t border-border p-3">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">Time</span>
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={23}
                                placeholder="HH"
                                className="w-16 text-center"
                                value={
                                  field.value
                                    ? String(field.value.getHours()).padStart(
                                        2,
                                        "0",
                                      )
                                    : ""
                                }
                                onChange={(e) => {
                                  const hours = Math.min(
                                    23,
                                    Math.max(0, parseInt(e.target.value) || 0),
                                  );
                                  const date = field.value || new Date();
                                  const newDate = new Date(date);
                                  newDate.setHours(hours);
                                  field.onChange(newDate);
                                }}
                              />
                              <span className="text-lg font-medium">:</span>
                              <Input
                                type="number"
                                min={0}
                                max={59}
                                placeholder="MM"
                                className="w-16 text-center"
                                value={
                                  field.value
                                    ? String(field.value.getMinutes()).padStart(
                                        2,
                                        "0",
                                      )
                                    : ""
                                }
                                onChange={(e) => {
                                  const minutes = Math.min(
                                    59,
                                    Math.max(0, parseInt(e.target.value) || 0),
                                  );
                                  const date = field.value || new Date();
                                  const newDate = new Date(date);
                                  newDate.setMinutes(minutes);
                                  field.onChange(newDate);
                                }}
                              />
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                      <p className="text-sm text-muted-foreground">
                        Leave empty for immediate joining.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-foreground"
                  >
                    Detection settings
                    {settingsOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4 space-y-4">
                  {features.length === 0 ? (
                    <p className="py-2 text-sm text-muted-foreground">
                      No features available
                    </p>
                  ) : (
                    features.map((feature) => (
                      <FormField
                        key={feature.id}
                        control={form.control}
                        name={`features.${feature.label}`}
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between rounded-lg border border-border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base font-medium">
                                {feature.name}
                              </FormLabel>
                              <p className="text-sm text-muted-foreground">
                                {feature.description}
                              </p>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={
                                  feature.label === "voice_authentication"
                                }
                                className="data-[state=checked]:bg-[#DD2590] data-[state=unchecked]:bg-gray-300 dark:data-[state=unchecked]:border dark:data-[state=unchecked]:border-gray-500 dark:data-[state=unchecked]:bg-gray-600"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    ))
                  )}

                  {form.formState.errors.features?.message ? (
                    <FormMessage>
                      {
                        form.formState.errors.features
                          .message as unknown as string
                      }
                    </FormMessage>
                  ) : null}
                </CollapsibleContent>
              </Collapsible>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  className="bg-[#DD2590] text-white hover:bg-[#C11574]"
                  disabled={createBotMutation.isPending}
                >
                  {createBotMutation.isPending
                    ? "Creating..."
                    : "Create an Agent"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
